/* station-chat-web-push — browser-side push subscription.
 *
 * Registers the service worker, subscribes via the VAPID public key (fetched
 * from the BFF), and hands the PushSubscription to the webpush-worker by sending
 * a `webpush:subscribe` / `webpush:unsubscribe` ACTION over the chat WS. The BFF
 * INTERCEPTS that frame and routes it station-addressed to the WebPushWorker
 * helper (aveq-agent/bff/webpush.py) — the old global 'webpush:' namespace was
 * retired with the app_service tier, so the browser contract is unchanged but the
 * frame no longer reaches qbric as a namespace action. The actual
 * VAPID/encryption/send live in the out-of-scope webpush-worker
 * (documentation/webpush-worker-spec.md).
 *
 * Everything here is best-effort + guarded: a browser without push support, a
 * denied permission, or a not-yet-deployed worker must NEVER break the chat.
 */

export const SW_URL = '/service-worker.js';
export const PUBKEY_URL = '/api/v1/webpush/pubkey';

export function pushSupported() {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;
}

/* Current notification permission: 'granted' | 'denied' | 'default' |
 * 'unsupported'. 'default' means nobody has been asked yet — the ONLY state in
 * which an opt-in control should be offered.
 *
 * webpush-client-gaps: this exists because permission and subscription are now
 * decided in two different places. `ensureSubscribed` runs unattended from a
 * mount effect and must never prompt (see below); the UI reads this to decide
 * whether to show a control the user can click. */
export function pushPermission() {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/* "Not now" persistence for the opt-in bar (webpush-client-gaps review).
 *
 * PER-STATION, deliberately: dismissing the offer in one room shouldn't hide it
 * in another room the user might actually want notifications for — the annoyance
 * being fixed is re-nagging on every revisit to the SAME room, not the existence
 * of the offer. Keyed by station id.
 *
 * Every access is wrapped: Safari private mode throws on setItem, and
 * localStorage can be absent entirely (SSR / disabled storage). A throw here
 * must never break the room, so a storage failure degrades to "not dismissed"
 * (the offer shows) rather than an exception. */
const _DISMISS_PREFIX = 'webpush.optin.dismissed.';

export function isPushOptInDismissed(stationId) {
  try {
    return window.localStorage.getItem(_DISMISS_PREFIX + stationId) === '1';
  } catch (e) {
    return false;
  }
}

export function dismissPushOptIn(stationId) {
  try {
    window.localStorage.setItem(_DISMISS_PREFIX + stationId, '1');
  } catch (e) { /* private mode / no storage: bar just reappears next visit */ }
}

// VAPID public key (base64url) → Uint8Array for applicationServerKey.
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function fetchPublicKey() {
  const res = await fetch(PUBKEY_URL, { credentials: 'include' });
  if (!res.ok) return '';
  const data = await res.json();
  return (data && data.public_key) || '';
}

/* This browser's PushSubscription, made if needed, or null. Prompts only when asked
 * (`requestPermission`) — call that from a click: Safari refuses a prompt without a user
 * gesture, silently. Never throws.
 */
async function deviceSubscription(requestPermission) {
  try {
    if (!pushSupported()) return null;
    if (Notification.permission === 'denied') return null;
    if (Notification.permission === 'default') {
      if (!requestPermission) return null;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return null;
    }
    const reg = await navigator.serviceWorker.register(SW_URL);
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const pubkey = await fetchPublicKey();
      if (!pubkey) return null;  // worker / VAPID not configured yet
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,  // required: every push shows a notification
        applicationServerKey: urlBase64ToUint8Array(pubkey),
      });
    }
    return sub;
  } catch (e) {
    return null;
  }
}

/* Ensure this browser is subscribed for `stationId`, handing the subscription to
 * the worker via `send(actionUri, params)` (the WS global-action dispatcher).
 * Idempotent — an existing PushSubscription is reused (one device = one). Returns
 * true if a subscription was registered, false otherwise. Never throws.
 */
export async function ensureSubscribed(stationId, accountId, send, opts = {}) {
  const { requestPermission = false } = opts;
  if (!accountId) return false;
  const sub = await deviceSubscription(requestPermission);
  if (!sub) return false;
  try {
    send('webpush:subscribe', {
      account_id: accountId,
      subscription: sub.toJSON(),
      station_id: stationId,
    });
    return true;
  } catch (e) {
    return false;  // best-effort: never break the chat over a push hiccup
  }
}

/* The same subscription over HTTP, for pages without a chat socket (Settings). Returns
 * true when the server took it. Never throws.
 */
export async function subscribeThisDevice(opts = {}) {
  const sub = await deviceSubscription(Boolean(opts.requestPermission));
  if (!sub) return false;
  try {
    const r = await fetch('/api/v1/webpush/subscribe', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription: sub.toJSON() }),
    });
    return r.ok;
  } catch (e) {
    return false;
  }
}

/* Tell the worker to drop THIS browser's subscription — called on logout so
 * notifications stop ("until logout"). Endpoint-scoped (per-device): logging out
 * on a laptop must not kill the same account's phone pushes, so we pass this
 * device's `endpoint` and the worker removes only the matching row. If no
 * subscription exists on this device there's nothing to drop (no-op). Best-effort;
 * never throws. Async so the caller can await the WS send before tearing down the
 * session. */
export async function unsubscribeAccount(stationId, accountId, send) {
  try {
    if (!accountId || !pushSupported()) return;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    const sub = reg && (await reg.pushManager.getSubscription());
    if (!sub) return;  // nothing subscribed on this device → nothing to unsubscribe
    send('webpush:unsubscribe', {
      account_id: accountId,
      station_id: stationId,
      endpoint: sub.endpoint,  // device-scoped: drop only this browser's row
    });
  } catch (e) { /* no-op: best-effort */ }
}
